import React, { createContext, useContext, useState } from 'react';

const MessageNotificationsContext = createContext();

export const useMessageNotifications = () => {
    const context = useContext(MessageNotificationsContext);
    if (!context) {
        throw new Error('useMessageNotifications must be used within MessageNotificationsProvider');
    }
    return context;
};

export const MessageNotificationsProvider = ({ children }) => {
    const [messageNotifications, setMessageNotifications] = useState([]);

    // Fonction pour jouer le son de notification
    const playNotificationSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Créer un son plus agréable et moderne (style WhatsApp/Telegram)
            const createTone = (frequency, startTime, duration, volume = 0.1) => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.setValueAtTime(frequency, startTime);
                oscillator.type = 'sine'; // Son plus doux

                // Envelope ADSR pour un son plus naturel
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01); // Attack
                gainNode.gain.linearRampToValueAtTime(volume * 0.8, startTime + 0.1); // Decay
                gainNode.gain.linearRampToValueAtTime(volume * 0.8, startTime + duration - 0.05); // Sustain
                gainNode.gain.linearRampToValueAtTime(0, startTime + duration); // Release

                oscillator.start(startTime);
                oscillator.stop(startTime + duration);

                return oscillator;
            };

            // Mélodie de notification agréable (Do-Mi-Sol)
            const now = audioContext.currentTime;
            createTone(523.25, now, 0.15, 0.08); // Do5
            createTone(659.25, now + 0.08, 0.15, 0.08); // Mi5
            createTone(783.99, now + 0.16, 0.2, 0.06); // Sol5

        } catch (error) {
            // Fallback : son plus agréable en base64
            try {
                // Son de notification plus mélodieux
                const audio = new Audio('data:audio/wav;base64,UklGRrQGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YZAGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmMfCDuX3PO9diEHYLvp66hVFA==');
                audio.volume = 0.2;
                audio.play().catch(() => {
                    console.log('Sons désactivés par le navigateur');
                });
            } catch (fallbackError) {
                console.log('Impossible de jouer le son de notification');
            }
        }
    };

    // Ajouter une notification de message (avec déduplication)
    const addMessageNotification = (message, senderInfo) => {
        const messageId = message.id || message.timestamp || Date.now();
        const notificationId = `msg_${messageId}`;

        setMessageNotifications(prev => {
            // Vérifier si une notification existe déjà pour ce message
            const existingNotification = prev.find(notif => notif.id === notificationId);
            if (existingNotification) {
                return prev; // Retourner l'état actuel sans changement
            }

            const notification = {
                id: notificationId,
                type: 'message',
                title: `${senderInfo.username} - ${senderInfo.erpName}`,
                description: message.text.length > 50 ? `${message.text.substring(0, 50)}...` : message.text,
                conversationId: message.conversationId,
                messageId: messageId, // Garder l'ID du message pour référence
                timestamp: new Date().toISOString(),
                icon: 'message',
                isRead: false
            };

            // Jouer le son de notification pour les nouvelles notifications
            playNotificationSound();

            return [notification, ...prev.slice(0, 19)]; // Garder max 20 notifications
        });
    };

    // Marquer une notification comme lue
    const markNotificationAsRead = (notificationId) => {
        setMessageNotifications(prev =>
            prev.map(notif =>
                notif.id === notificationId
                    ? { ...notif, isRead: true }
                    : notif
            )
        );
    };

    // Supprimer une notification
    const removeNotification = (notificationId) => {
        setMessageNotifications(prev =>
            prev.filter(notif => notif.id !== notificationId)
        );
    };

    // Supprimer toutes les notifications d'une conversation
    const removeNotificationsByConversation = (conversationId) => {
        setMessageNotifications(prev =>
            prev.filter(notif => notif.conversationId !== conversationId)
        );
    };

    // Nettoyer les anciennes notifications (plus de 24h)
    const cleanOldNotifications = () => {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        setMessageNotifications(prev =>
            prev.filter(notif => new Date(notif.timestamp) > oneDayAgo)
        );
    };

    const value = {
        messageNotifications,
        addMessageNotification,
        markNotificationAsRead,
        removeNotification,
        removeNotificationsByConversation,
        cleanOldNotifications
    };

    return (
        <MessageNotificationsContext.Provider value={value}>
            {children}
        </MessageNotificationsContext.Provider>
    );
}; 